import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, Users, Wrench, BarChart3, FileText, Plus, Edit2, Trash2, LogOut, Loader2, Save, Search, Lock, Unlock, Shield, ShieldOff, Key, Home, ChevronLeft, ChevronRight, Crown, Star, BookTemplate, Settings, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AuthContext, API } from "@/App";
import { toast } from "sonner";
import { WebsearchTab } from "@/components/admin/WebsearchTab";
import { SettingsTab } from "@/components/admin/SettingsTab";

const AdminDashboard = () => {
  const { user, logout, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Content pages
  const [termsContent, setTermsContent] = useState("");
  const [privacyContent, setPrivacyContent] = useState("");
  const [faqContent, setFaqContent] = useState("");
  const [disclaimerContent, setDisclaimerContent] = useState("");
  const [contactContent, setContactContent] = useState("");
  const [refundContent, setRefundContent] = useState("");

  // Tool dialog
  const [editingTool, setEditingTool] = useState(null);
  const [showToolDialog, setShowToolDialog] = useState(false);

  // User management
  const [userSearch, setUserSearch] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newUserPassword, setNewUserPassword] = useState("");

  // Admin password
  const [showAdminPasswordDialog, setShowAdminPasswordDialog] = useState(false);
  const [currentAdminPassword, setCurrentAdminPassword] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [confirmAdminPassword, setConfirmAdminPassword] = useState("");

  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateForm, setTemplateForm] = useState({});

  useEffect(() => {
    if (!authLoading && user && !user.is_admin) {
      toast.error("Admin access required");
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  const emptyTool = {
    name: "", category: "General", description: "", website_url: "",
    price: "Free", programming_languages: [], frameworks: [], capabilities: [],
    keywords_and_techniques: [], cannot_do: [], context_window: "",
    file_support: [], seo_capable: false, supports_agentic: false,
    supports_programmatic: false, affiliate_link: "", sources: [],
    version: "", general_info: "", reasoning_model: "general", features: []
  };

  const [toolForm, setToolForm] = useState(emptyTool);

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [toolsRes, analyticsRes, termsRes, privacyRes, faqRes, disclaimerRes, templatesRes, contactRes, refundRes] = await Promise.all([
        fetch(`${API}/tools`, { credentials: "include" }),
        fetch(`${API}/admin/analytics`, { credentials: "include" }),
        fetch(`${API}/admin/content/terms`, { credentials: "include" }),
        fetch(`${API}/admin/content/privacy`, { credentials: "include" }),
        fetch(`${API}/admin/content/faq`, { credentials: "include" }),
        fetch(`${API}/admin/content/disclaimer`, { credentials: "include" }),
        fetch(`${API}/prompt-templates`, { credentials: "include" }),
        fetch(`${API}/admin/content/contact`, { credentials: "include" }),
        fetch(`${API}/admin/content/refund`, { credentials: "include" })
      ]);
      if (toolsRes.ok) setTools(await toolsRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (termsRes.ok) { const d = await termsRes.json(); setTermsContent(d.content || ""); }
      if (privacyRes.ok) { const d = await privacyRes.json(); setPrivacyContent(d.content || ""); }
      if (faqRes.ok) { const d = await faqRes.json(); setFaqContent(d.content || ""); }
      if (disclaimerRes.ok) { const d = await disclaimerRes.json(); setDisclaimerContent(d.content || ""); }
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (contactRes.ok) { const d = await contactRes.json(); setContactContent(d.content || ""); }
      if (refundRes.ok) { const d = await refundRes.json(); setRefundContent(d.content || ""); }
      await fetchUsers();
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (search = "", filter = "all") => {
    try {
      let url = `${API}/admin/users`;
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filter !== "all") params.append("filter_type", filter);
      if (params.toString()) url += `?${params.toString()}`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) setUsers(await res.json());
    } catch (e) {}
  };

  const handleSaveTool = async () => {
    try {
      const method = editingTool ? "PUT" : "POST";
      const url = editingTool ? `${API}/tools/${editingTool.tool_id}` : `${API}/tools`;
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(toolForm)
      });
      if (res.ok || res.status === 201) {
        toast.success(editingTool ? "Tool updated!" : "Tool created!");
        setShowToolDialog(false); setEditingTool(null); setToolForm(emptyTool);
        fetchAllData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to save tool");
      }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleDeleteTool = async (toolId) => {
    if (!window.confirm("Delete this AI tool?")) return;
    try {
      const res = await fetch(`${API}/tools/${toolId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Tool deleted!"); fetchAllData(); }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleUpdateUserSubscription = async (userId, tier) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/subscription`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tier })
      });
      if (res.ok) { toast.success("Subscription updated!"); fetchUsers(userSearch, userFilter); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleToggleVip = async (userId, isVip) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/vip`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ is_vip: isVip })
      });
      if (res.ok) { toast.success(isVip ? "VIP granted!" : "VIP revoked!"); fetchUsers(userSearch, userFilter); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/admin`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ is_admin: isAdmin })
      });
      if (res.ok) { toast.success(isAdmin ? "Admin granted!" : "Admin revoked!"); fetchUsers(userSearch, userFilter); }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleToggleSupport = async (userId, isSupport) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/support`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ is_support: isSupport })
      });
      if (res.ok) { toast.success(isSupport ? "Support role granted" : "Support role revoked"); fetchUsers(userSearch, userFilter); }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleToggleLock = async (userId, isLocked) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/lock`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ is_locked: isLocked })
      });
      if (res.ok) { toast.success(isLocked ? "Locked!" : "Unlocked!"); fetchUsers(userSearch, userFilter); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleChangeUserPassword = async () => {
    if (!newUserPassword || newUserPassword.length < 6) { toast.error("Min 6 characters"); return; }
    try {
      const res = await fetch(`${API}/admin/users/${selectedUserForPassword.user_id}/password`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ new_password: newUserPassword })
      });
      if (res.ok) { toast.success("Password changed!"); setShowPasswordDialog(false); setNewUserPassword(""); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleChangeAdminPassword = async () => {
    if (newAdminPassword !== confirmAdminPassword) { toast.error("Passwords don't match"); return; }
    if (newAdminPassword.length < 6) { toast.error("Min 6 characters"); return; }
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ current_password: currentAdminPassword || null, new_password: newAdminPassword })
      });
      if (res.ok) { toast.success("Password changed!"); setShowAdminPasswordDialog(false); setCurrentAdminPassword(""); setNewAdminPassword(""); setConfirmAdminPassword(""); }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleSaveContent = async (page, content) => {
    try {
      const res = await fetch(`${API}/admin/content/${page}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ content })
      });
      if (res.ok) toast.success(`${page} page updated!`);
      else toast.error("Failed to save");
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleSaveTemplate = async () => {
    try {
      const res = await fetch(`${API}/prompt-templates/${editingTemplate.template_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(templateForm)
      });
      if (res.ok) { toast.success("Template updated!"); setShowTemplateDialog(false); fetchAllData(); }
      else toast.error("Failed to save template");
    } catch (e) { toast.error("Something went wrong"); }
  };

  const openEditTool = (tool) => {
    setEditingTool(tool);
    setToolForm({
      name: tool.name || "", category: tool.category || "General", description: tool.description || "",
      website_url: tool.website_url || "", price: tool.price || "Free",
      programming_languages: tool.programming_languages || [], frameworks: tool.frameworks || [],
      capabilities: tool.capabilities || [], keywords_and_techniques: tool.keywords_and_techniques || [],
      cannot_do: tool.cannot_do || [], context_window: tool.context_window || "",
      file_support: tool.file_support || [], seo_capable: tool.seo_capable || false,
      supports_agentic: tool.supports_agentic || false, supports_programmatic: tool.supports_programmatic || false,
      affiliate_link: tool.affiliate_link || "", sources: tool.sources || [],
      version: tool.version || "", general_info: tool.general_info || "",
      reasoning_model: tool.reasoning_model || "general", features: tool.features || []
    });
    setShowToolDialog(true);
  };

  const openNewTool = () => { setEditingTool(null); setToolForm(emptyTool); setShowToolDialog(true); };

  const openEditTemplate = (tpl) => {
    setEditingTemplate(tpl);
    setTemplateForm({ name: tpl.name, description: tpl.description, structure: tpl.structure, fields: tpl.fields, is_active: tpl.is_active });
    setShowTemplateDialog(true);
  };

  if (loading || authLoading) {
    return <div className="min-h-screen bg-dark flex items-center justify-center"><Loader2 className="w-8 h-8 text-purple-400 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-dark">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white/60 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => navigate(1)} className="text-white/60 hover:text-white"><ChevronRight className="w-5 h-5" /></Button>
              </div>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-700 flex items-center justify-center"><Compass className="w-4 h-4 text-white" /></div>
                <span className="font-inter font-bold text-lg">promptviser</span>
              </Link>
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">Admin</Badge>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/"><Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><Home className="w-5 h-5" /></Button></Link>
              <Link to="/dashboard"><Button variant="outline" size="sm" className="border-white/20 text-xs">User Dashboard</Button></Link>
              <Button variant="outline" size="sm" onClick={() => setShowAdminPasswordDialog(true)} className="border-white/20 text-xs"><Key className="w-3.5 h-3.5 mr-1" />Password</Button>
              <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} className="text-white/60 text-xs"><LogOut className="w-3.5 h-3.5 mr-1" />Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <h1 className="font-inter font-bold text-2xl mb-6">Admin Dashboard</h1>

        {/* Stats */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <div className="bg-dark-paper border border-white/10 rounded-xl p-4">
              <p className="text-white/40 text-xs mb-1">Users</p>
              <p className="font-inter font-bold text-2xl">{analytics.total_users}</p>
            </div>
            <div className="bg-dark-paper border border-white/10 rounded-xl p-4">
              <p className="text-white/40 text-xs mb-1">AI Tools</p>
              <p className="font-inter font-bold text-2xl">{analytics.total_tools}</p>
            </div>
            <div className="bg-dark-paper border border-white/10 rounded-xl p-4">
              <p className="text-white/40 text-xs mb-1">Searches</p>
              <p className="font-inter font-bold text-2xl">{analytics.total_searches}</p>
            </div>
            <div className="bg-dark-paper border border-purple-500/30 rounded-xl p-4">
              <p className="text-purple-400/70 text-xs mb-1">MRR</p>
              <p className="font-inter font-bold text-2xl text-purple-300">${analytics.mrr}</p>
            </div>
            <div className="bg-dark-paper border border-neon/20 rounded-xl p-4">
              <p className="text-neon/70 text-xs mb-1">Pro</p>
              <p className="font-inter font-bold text-2xl text-neon">{analytics.subscriptions?.pro || 0}</p>
            </div>
            <div className="bg-dark-paper border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-400/70 text-xs mb-1">Premium+</p>
              <p className="font-inter font-bold text-2xl text-amber-400">{(analytics.subscriptions?.premium || 0) + (analytics.subscriptions?.team || 0)}</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="tools" className="w-full">
          <TabsList className="bg-dark-paper border border-white/10 p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="tools" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><Wrench className="w-3.5 h-3.5 mr-1" />AI Database</TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><BookTemplate className="w-3.5 h-3.5 mr-1" />Prompt Templates</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><Users className="w-3.5 h-3.5 mr-1" />Users</TabsTrigger>
            <TabsTrigger value="websearch" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs" data-testid="websearch-tab-trigger"><Search className="w-3.5 h-3.5 mr-1" />Websearch</TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><FileText className="w-3.5 h-3.5 mr-1" />Content</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs" data-testid="settings-tab-trigger"><Settings className="w-3.5 h-3.5 mr-1" />System Settings</TabsTrigger>
          </TabsList>

          {/* AI Database Tab */}
          <TabsContent value="tools" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-inter font-semibold text-lg">AI Tools ({tools.length})</h2>
              <Button onClick={openNewTool} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="add-tool-button"><Plus className="w-3.5 h-3.5 mr-1" />Add AI Tool</Button>
            </div>
            {tools.length === 0 ? (
              <div className="text-center py-12 bg-dark-paper border border-white/10 rounded-xl">
                <Wrench className="w-10 h-10 text-white/15 mx-auto mb-3" />
                <h3 className="font-semibold mb-1">No AI tools yet</h3>
                <p className="text-white/40 text-sm mb-3">Add your first AI tool to get started</p>
                <Button onClick={openNewTool} className="bg-purple-700 text-white hover:bg-purple-600 text-xs"><Plus className="w-3.5 h-3.5 mr-1" />Add Tool</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {tools.map((tool) => (
                  <div key={tool.tool_id} className="bg-dark-paper border border-white/10 rounded-xl p-4 flex items-start justify-between gap-4" data-testid={`tool-item-${tool.tool_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold">{tool.name}</h3>
                        <Badge variant="outline" className="text-[10px] border-white/20">{tool.category}</Badge>
                        <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-300">{tool.price}</Badge>
                        {tool.supports_agentic && <Badge className="bg-purple-500/15 text-purple-300 text-[10px]">Agentic</Badge>}
                        {tool.supports_programmatic && <Badge className="bg-neon/10 text-neon text-[10px]">Programmatic</Badge>}
                        {tool.seo_capable && <Badge className="bg-blue-500/15 text-blue-300 text-[10px]">SEO</Badge>}
                      </div>
                      <p className="text-white/50 text-xs mb-1.5 line-clamp-1">{tool.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {tool.programming_languages?.slice(0, 5).map((l, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/40">{l}</span>)}
                        {tool.programming_languages?.length > 5 && <span className="text-[10px] text-white/30">+{tool.programming_languages.length - 5}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditTool(tool)} className="border-white/20"><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteTool(tool.tool_id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Prompt Templates Tab */}
          <TabsContent value="templates" className="mt-6">
            <h2 className="font-inter font-semibold text-lg mb-4">Prompt Templates ({templates.length})</h2>
            <div className="space-y-3">
              {templates.map((tpl) => (
                <div key={tpl.template_id} className="bg-dark-paper border border-white/10 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm">{tpl.name}</h3>
                        <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-300 capitalize">{tpl.tier}</Badge>
                        <Badge className={`text-[10px] ${tpl.is_active ? "bg-neon/10 text-neon" : "bg-red-500/10 text-red-400"}`}>{tpl.is_active ? "Active" : "Inactive"}</Badge>
                      </div>
                      <p className="text-white/40 text-xs mb-1">{tpl.description}</p>
                      <p className="text-white/30 text-[10px] font-mono">{tpl.structure}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openEditTemplate(tpl)} className="border-white/20"><Edit2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <h2 className="font-inter font-semibold text-lg">Users ({users.length})</h2>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                  <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchUsers(userSearch, userFilter)}
                    placeholder="Search..." className="pl-8 bg-dark-subtle border-white/10 text-xs w-full sm:w-48" />
                </div>
                <Select value={userFilter} onValueChange={(v) => { setUserFilter(v); fetchUsers(userSearch, v); }}>
                  <SelectTrigger className="w-28 bg-dark-subtle border-white/10 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="subscribed">Subscribed</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                    <SelectItem value="vip">VIP</SelectItem>
                    <SelectItem value="locked">Locked</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => fetchUsers(userSearch, userFilter)} size="sm" className="bg-purple-700 text-white text-xs"><Search className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            {users.length === 0 ? (
              <div className="text-center py-12 bg-dark-paper border border-white/10 rounded-xl">
                <Users className="w-10 h-10 text-white/15 mx-auto mb-3" />
                <h3 className="font-semibold mb-1">No users found</h3>
              </div>
            ) : (
              <div className="bg-dark-paper border border-white/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-3 text-white/40 font-medium text-xs">User</th>
                        <th className="text-left p-3 text-white/40 font-medium text-xs">Email</th>
                        <th className="text-left p-3 text-white/40 font-medium text-xs">Plan</th>
                        <th className="text-left p-3 text-white/40 font-medium text-xs">VIP</th>
                        <th className="text-left p-3 text-white/40 font-medium text-xs">Admin</th>
                        <th className="text-left p-3 text-white/40 font-medium text-xs">Support</th>
                        <th className="text-left p-3 text-white/40 font-medium text-xs">Lock</th>
                        <th className="text-left p-3 text-white/40 font-medium text-xs">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-purple-900/50 flex items-center justify-center text-purple-300 text-xs font-semibold">{u.name?.[0] || "U"}</div>
                              <div>
                                <span className="text-sm font-medium">{u.name}</span>
                                {u.is_admin && <Badge className="ml-1 text-[9px] bg-red-500/20 text-red-400">Admin</Badge>}
                                {u.is_vip && <Badge className="ml-1 text-[9px] bg-neon/10 text-neon">VIP</Badge>}
                                {u.is_locked && <Badge className="ml-1 text-[9px] bg-amber-500/20 text-amber-400">Locked</Badge>}
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-white/50 text-xs">{u.email}</td>
                          <td className="p-3">
                            <Select value={u.subscription_tier || "free"} onValueChange={(val) => handleUpdateUserSubscription(u.user_id, val)}>
                              <SelectTrigger className="w-24 h-7 text-[10px] bg-dark-subtle border-white/10"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">Free</SelectItem>
                                <SelectItem value="pro">Pro</SelectItem>
                                <SelectItem value="premium">Premium</SelectItem>
                                <SelectItem value="team">Team</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3">
                            <Button variant="ghost" size="sm" onClick={() => handleToggleVip(u.user_id, !u.is_vip)} className={u.is_vip ? "text-neon" : "text-white/30"}>
                              <Star className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                          <td className="p-3">
                            <Button variant="ghost" size="sm" onClick={() => handleToggleAdmin(u.user_id, !u.is_admin)} disabled={u.user_id === user?.user_id} className={u.is_admin ? "text-red-400" : "text-white/30"}>
                              {u.is_admin ? <Shield className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                            </Button>
                          </td>
                          <td className="p-3">
                            <Button variant="ghost" size="sm" onClick={() => handleToggleSupport(u.user_id, !u.is_support)} className={u.is_support ? "text-blue-400" : "text-white/30"} data-testid={`toggle-support-${u.user_id}`} title="Customer Support role">
                              <Headphones className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                          <td className="p-3">
                            <Button variant="ghost" size="sm" onClick={() => handleToggleLock(u.user_id, !u.is_locked)} disabled={u.user_id === user?.user_id} className={u.is_locked ? "text-amber-400" : "text-white/30"}>
                              {u.is_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                            </Button>
                          </td>
                          <td className="p-3">
                            <Button variant="outline" size="sm" onClick={() => { setSelectedUserForPassword(u); setShowPasswordDialog(true); }} className="border-white/20 text-[10px]">
                              <Key className="w-3 h-3 mr-0.5" />Pwd
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Websearch Tab */}
          <TabsContent value="websearch" className="mt-6">
            <WebsearchTab tools={tools} categories={[...new Set(tools.map(t => t.category).filter(Boolean))]} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <SettingsTab />
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="mt-6">
            <h2 className="font-inter font-semibold text-lg mb-4">Legal & Content Pages</h2>
            <div className="space-y-4">
              {[
                { key: "terms", label: "Terms of Service", val: termsContent, set: setTermsContent },
                { key: "privacy", label: "Privacy Policy", val: privacyContent, set: setPrivacyContent },
                { key: "refund", label: "Refund Policy", val: refundContent, set: setRefundContent },
                { key: "contact", label: "Contact Page", val: contactContent, set: setContactContent },
                { key: "faq", label: "FAQ", val: faqContent, set: setFaqContent },
                { key: "disclaimer", label: "Disclaimer", val: disclaimerContent, set: setDisclaimerContent }
              ].map(({ key, label, val, set }) => (
                <div key={key} className="bg-dark-paper border border-white/10 rounded-xl p-5">
                  <h3 className="font-semibold text-sm mb-3">{label}</h3>
                  <Textarea value={val} onChange={(e) => set(e.target.value)} className="min-h-[150px] bg-dark-subtle border-white/10 mb-3 font-mono text-xs" placeholder={`Edit ${label} (Markdown)`} />
                  <Button onClick={() => handleSaveContent(key, val)} className="bg-purple-700 text-white hover:bg-purple-600 text-xs"><Save className="w-3.5 h-3.5 mr-1" />Save {label}</Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Tool Dialog */}
      <Dialog open={showToolDialog} onOpenChange={setShowToolDialog}>
        <DialogContent className="bg-dark-paper border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-inter">{editingTool ? "Edit AI Tool" : "Add AI Tool"}</DialogTitle>
            <DialogDescription>All fields per Instruction 4 schema</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name *</Label><Input value={toolForm.name} onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="e.g., Claude 3.5 Sonnet" data-testid="tool-name-input" /></div>
              <div><Label className="text-xs">Category</Label><Input value={toolForm.category} onChange={(e) => setToolForm({ ...toolForm, category: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="Text/Chat/Code" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Price</Label><Input value={toolForm.price} onChange={(e) => setToolForm({ ...toolForm, price: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="$20/month" /></div>
              <div><Label className="text-xs">Context Window</Label><Input value={toolForm.context_window} onChange={(e) => setToolForm({ ...toolForm, context_window: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="200K tokens" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Version</Label><Input value={toolForm.version} onChange={(e) => setToolForm({ ...toolForm, version: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="e.g., 3.5" data-testid="tool-version-input" /></div>
              <div>
                <Label className="text-xs">Reasoning Model</Label>
                <Select value={toolForm.reasoning_model} onValueChange={(v) => setToolForm({ ...toolForm, reasoning_model: v })}>
                  <SelectTrigger className="bg-dark-subtle border-white/10 text-xs" data-testid="tool-reasoning-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="claude_structured">Claude — structured, [REASONING] tags, persona</SelectItem>
                    <SelectItem value="gpt_conversational">GPT — conversational, examples</SelectItem>
                    <SelectItem value="copilot_context">Copilot — context + file structure, code blocks</SelectItem>
                    <SelectItem value="gemini_multimodal">Gemini — multimodal framing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">General Information</Label><Textarea value={toolForm.general_info} onChange={(e) => setToolForm({ ...toolForm, general_info: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="General information about this AI" data-testid="tool-general-info-input" /></div>
            <div><Label className="text-xs">Features (comma-separated)</Label><Input value={toolForm.features.join(", ")} onChange={(e) => setToolForm({ ...toolForm, features: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-dark-subtle border-white/10 text-sm" placeholder="vision, artifacts, code interpreter" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={toolForm.description} onChange={(e) => setToolForm({ ...toolForm, description: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="Brief description" /></div>
            <div><Label className="text-xs">Website URL</Label><Input value={toolForm.website_url} onChange={(e) => setToolForm({ ...toolForm, website_url: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="https://..." /></div>
            <div><Label className="text-xs">Affiliate Link</Label><Input value={toolForm.affiliate_link || ""} onChange={(e) => setToolForm({ ...toolForm, affiliate_link: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="https://..." /></div>
            <div><Label className="text-xs">Programming Languages (comma-separated)</Label><Input value={toolForm.programming_languages.join(", ")} onChange={(e) => setToolForm({ ...toolForm, programming_languages: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-dark-subtle border-white/10 text-sm" placeholder="Python, JavaScript, Swift" /></div>
            <div><Label className="text-xs">Frameworks (comma-separated)</Label><Input value={toolForm.frameworks.join(", ")} onChange={(e) => setToolForm({ ...toolForm, frameworks: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-dark-subtle border-white/10 text-sm" placeholder="React, Django, SwiftUI" /></div>
            <div><Label className="text-xs">Capabilities (comma-separated)</Label><Input value={toolForm.capabilities.join(", ")} onChange={(e) => setToolForm({ ...toolForm, capabilities: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-dark-subtle border-white/10 text-sm" placeholder="code_generation, debugging, documentation" /></div>
            <div><Label className="text-xs">Keywords & Techniques (comma-separated)</Label><Input value={toolForm.keywords_and_techniques.join(", ")} onChange={(e) => setToolForm({ ...toolForm, keywords_and_techniques: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-dark-subtle border-white/10 text-sm" placeholder="chain_of_thought, reasoning_tags" /></div>
            <div><Label className="text-xs">Cannot Do (comma-separated)</Label><Input value={toolForm.cannot_do.join(", ")} onChange={(e) => setToolForm({ ...toolForm, cannot_do: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-dark-subtle border-white/10 text-sm" placeholder="execute_code, access_database" /></div>
            <div><Label className="text-xs">File Support (comma-separated)</Label><Input value={toolForm.file_support.join(", ")} onChange={(e) => setToolForm({ ...toolForm, file_support: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-dark-subtle border-white/10 text-sm" placeholder="PDF, DOCX, JSON, PY" /></div>
            <div><Label className="text-xs">Sources (comma-separated URLs)</Label><Input value={toolForm.sources.join(", ")} onChange={(e) => setToolForm({ ...toolForm, sources: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-dark-subtle border-white/10 text-sm" placeholder="https://docs.example.com" /></div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-xs"><Switch checked={toolForm.seo_capable} onCheckedChange={(v) => setToolForm({ ...toolForm, seo_capable: v })} /><span>SEO Capable</span></label>
              <label className="flex items-center gap-2 text-xs"><Switch checked={toolForm.supports_agentic} onCheckedChange={(v) => setToolForm({ ...toolForm, supports_agentic: v })} /><span>Supports Agentic</span></label>
              <label className="flex items-center gap-2 text-xs"><Switch checked={toolForm.supports_programmatic} onCheckedChange={(v) => setToolForm({ ...toolForm, supports_programmatic: v })} /><span>Supports Programmatic</span></label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowToolDialog(false)} className="border-white/20 text-xs">Cancel</Button>
            <Button onClick={handleSaveTool} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" disabled={!toolForm.name || !toolForm.website_url} data-testid="save-tool-button"><Save className="w-3.5 h-3.5 mr-1" />{editingTool ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="bg-dark-paper border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-inter text-sm">Edit Template: {editingTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div><Label className="text-xs">Name</Label><Input value={templateForm.name || ""} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={templateForm.description || ""} onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" /></div>
            <div><Label className="text-xs">Structure</Label><Input value={templateForm.structure || ""} onChange={(e) => setTemplateForm({ ...templateForm, structure: e.target.value })} className="bg-dark-subtle border-white/10 text-sm font-mono" /></div>
            <div><Label className="text-xs">Fields (JSON)</Label><Textarea value={JSON.stringify(templateForm.fields || {}, null, 2)} onChange={(e) => { try { setTemplateForm({ ...templateForm, fields: JSON.parse(e.target.value) }); } catch {} }} className="bg-dark-subtle border-white/10 text-xs font-mono min-h-[120px]" /></div>
            <label className="flex items-center gap-2 text-xs"><Switch checked={templateForm.is_active} onCheckedChange={(v) => setTemplateForm({ ...templateForm, is_active: v })} /><span>Active</span></label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)} className="border-white/20 text-xs">Cancel</Button>
            <Button onClick={handleSaveTemplate} className="bg-purple-700 text-white hover:bg-purple-600 text-xs"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Dialogs */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-dark-paper border-white/10">
          <DialogHeader><DialogTitle className="text-sm">Change Password for {selectedUserForPassword?.name}</DialogTitle></DialogHeader>
          <div className="py-3"><Label className="text-xs">New Password</Label><Input type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} className="bg-dark-subtle border-white/10 mt-1 text-sm" placeholder="Min. 6 characters" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="border-white/20 text-xs">Cancel</Button>
            <Button onClick={handleChangeUserPassword} className="bg-purple-700 text-white hover:bg-purple-600 text-xs">Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAdminPasswordDialog} onOpenChange={setShowAdminPasswordDialog}>
        <DialogContent className="bg-dark-paper border-white/10">
          <DialogHeader><DialogTitle className="text-sm">Change Your Password</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div><Label className="text-xs">Current Password</Label><Input type="password" value={currentAdminPassword} onChange={(e) => setCurrentAdminPassword(e.target.value)} className="bg-dark-subtle border-white/10 mt-1 text-sm" placeholder="Leave empty if Google sign-up" /></div>
            <div><Label className="text-xs">New Password</Label><Input type="password" value={newAdminPassword} onChange={(e) => setNewAdminPassword(e.target.value)} className="bg-dark-subtle border-white/10 mt-1 text-sm" /></div>
            <div><Label className="text-xs">Confirm</Label><Input type="password" value={confirmAdminPassword} onChange={(e) => setConfirmAdminPassword(e.target.value)} className="bg-dark-subtle border-white/10 mt-1 text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminPasswordDialog(false)} className="border-white/20 text-xs">Cancel</Button>
            <Button onClick={handleChangeAdminPassword} className="bg-purple-700 text-white hover:bg-purple-600 text-xs">Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
